/**
 * Page Transition Wrapper (Phase 10 — SaaS Polish)
 * Smooth fade transitions between routes.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';

interface PageTransitionProps {
  children: ReactNode;
}

export default function PageTransition({ children }: PageTransitionProps) {
  const location = useLocation();
  const [displayChildren, setDisplayChildren] = useState(children);
  const [fadeState, setFadeState] = useState<'in' | 'out'>('in');
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip fade animation on initial render to prevent hydration layout shift
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    // When location changes, fade out then swap content
    if (location.pathname) {
      setFadeState('out');
      const timer = setTimeout(() => {
        setDisplayChildren(children);
        setFadeState('in');
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, children]);

  return (
    <div
      className="transition-opacity duration-150 ease-out"
      style={{ opacity: fadeState === 'in' ? 1 : 0 }}
    >
      {displayChildren}
    </div>
  );
}
